/*
 * Gedeelde communicatie-bouwstenen — zie /app/docs/COMMUNICATION.md
 * Elke e-mail/WhatsApp-modal in de app gebruikt deze onderdelen zodat lay-out,
 * mogelijkheden (templates, tags, cc/bcc, bijlagen, links) en test-ids consistent zijn.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FileText, Paperclip, Loader2, X, Image as ImageIcon } from "lucide-react";

// Standaard tag-sets per context (weergave = wat letterlijk wordt ingevoegd)
export const TAGSETS = {
  person: ["{{voornaam}}", "{{naam}}", "{{product}}", "{{NAWT}}", "{{csm}}", "{csm}"],
  personSingle: ["{voornaam}", "{naam}", "{csm}"],
  event: ["{voornaam}", "{naam}", "{eventnaam}", "{eventdatum}", "{eventlocatie}", "{eventadres}", "{eventstarttijd}", "{eventeindtijd}"],
  eventEmail: ["{{voornaam}}", "{{naam}}", "{eventnaam}", "{eventdatum}", "{eventlocatie}", "{eventadres}", "{eventstarttijd}", "{eventeindtijd}"],
};

// Uitleg per tag (tooltip op de chips)
export const TAG_HELP = {
  "{{voornaam}}": "Voornaam van de ontvanger",
  "{voornaam}": "Voornaam van de ontvanger",
  "{{naam}}": "Volledige naam van de ontvanger",
  "{naam}": "Volledige naam van de ontvanger",
  "{{product}}": "Productnaam van de klant",
  "{product}": "Productnaam van de klant",
  "{{NAWT}}": "Volledig NAWT-blok van de klant: bedrijfsnaam (alleen zakelijk), naam, adres, postcode + plaats, e-mail en telefoon",
  "{{csm}}": "Jouw naam (de verzender van de mail)",
  "{csm}": "Naam van de toegewezen CSM van de klant",
};

/** NAWT-blok van een klant (regels met <br>): bedrijfsnaam (alleen zakelijk), naam, adres,
 *  postcode + plaats, e-mail, telefoon — lege regels worden overgeslagen. */
export const buildNawt = (c) => {
  if (!c) return "";
  return [
    c.customer_type === "B2B" ? c.company_name : null,
    c.name,
    c.address,
    [c.postcode, c.city].filter(Boolean).join(" "),
    c.email,
    c.phone,
  ].map((x) => (x || "").trim()).filter(Boolean).join("<br>");
};

/** Vult persoons-tags in — case-insensitief, {{tag}} én {tag}: voornaam/naam/product/NAWT;
 *  {{csm}} = verzender, {csm} = toegewezen CSM. */
const _dbl = (text, tag, val) => text.replace(new RegExp(`\\{\\{\\s*${tag}\\s*\\}\\}`, "gi"), val);
const _sgl = (text, tag, val) => text.replace(new RegExp(`\\{\\s*${tag}\\s*\\}`, "gi"), val);
export const fillPersonTags = (text, { name = "", product = "", sender = "", csm = "", nawt = "" } = {}) => {
  const voornaam = (name || "").split(" ")[0] || "";
  let out = text || "";
  out = _dbl(out, "csm", sender || csm || "");
  out = _sgl(out, "csm", csm || sender || "");
  for (const [tag, val] of [["voornaam", voornaam], ["naam", name || ""], ["product", product || ""], ["nawt", nawt || ""]]) {
    out = _sgl(_dbl(out, tag, val), tag, val);
  }
  return out;
};

export function TagChips({ tags, onInsert, testId = "comm-tags" }) {
  if (!tags?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
      <span className="text-[11px] text-slate-400">Tags:</span>
      {tags.map((t) => (
        <button key={t} type="button" onClick={() => onInsert(t)} title={TAG_HELP[t]}
          data-testid={`${testId}-${t.startsWith("{{") ? "" : "s-"}${t.replace(/[{}]/g, "")}`}
          className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-mono text-blue-700 hover:bg-blue-100 transition-colors">
          {t}
        </button>
      ))}
    </div>
  );
}

/** Sjabloon-select: gedeelde berichtsjablonen (admin) + gedeelde e-mailtemplates (heel het team). */
export function TemplatePicker({ kind, personal = false, onPick, testId = "comm-template-select" }) {
  const [shared, setShared] = useState([]);
  const [own, setOwn] = useState([]);
  useEffect(() => {
    api.get("/message-templates").then((r) => setShared((r.data || []).filter((t) => t.kind === kind))).catch(() => {});
    if (personal && kind === "email") api.get("/email-templates").then((r) => setOwn(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [kind, personal]);
  if (!shared.length && !own.length) return null;
  const pick = (val) => {
    const [src, id] = val.split(":");
    const t = (src === "p" ? own : shared).find((x) => x.id === id);
    if (t) onPick(t);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Template</Label>
      <select defaultValue="" data-testid={testId}
        onChange={(e) => { if (e.target.value) pick(e.target.value); e.target.value = ""; }}
        className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
        <option value="">Sjabloon invoegen…</option>
        {shared.length > 0 && (
          <optgroup label="Berichtsjablonen (gedeeld)">
            {shared.map((t) => <option key={t.id} value={`s:${t.id}`}>{t.name}</option>)}
          </optgroup>
        )}
        {own.length > 0 && (
          <optgroup label="E-mailtemplates (gedeeld met het team)">
            {own.map((t) => <option key={t.id} value={`p:${t.id}`}>{t.name}</option>)}
          </optgroup>
        )}
      </select>
    </div>
  );
}

export function SaveAsTemplate({ enabled, setEnabled, name, setName, testId = "comm-save-tpl", label = "Bewaar dit bericht als sjabloon" }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
          className="accent-blue-700" data-testid={`${testId}-check`} />
        {label}
      </label>
      {enabled && <Input value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Naam van het sjabloon" className="h-8 text-sm" data-testid={`${testId}-name`} />}
    </div>
  );
}

const readBase64 = (file) => new Promise((resolve) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1]);
  r.readAsDataURL(file);
});

/** Bijlagen als base64 ({filename, content, content_type, byte_size}) — direct meegestuurd via Resend. */
export function AttachmentsField({ value, setValue, maxTotalMB = 7, testId = "comm-attach" }) {
  const [busy, setBusy] = useState(false);
  const total = (value || []).reduce((s, a) => s + (a.byte_size || 0), 0);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const added = files.reduce((s, f) => s + f.size, 0);
    if (total + added > maxTotalMB * 1024 * 1024) {
      toast.error(`Bijlagen samen te groot (max ${maxTotalMB} MB)`);
      return;
    }
    setBusy(true);
    try {
      const items = await Promise.all(files.map(async (f) => ({
        filename: f.name, content: await readBase64(f),
        content_type: f.type || "application/octet-stream", byte_size: f.size,
      })));
      setValue([...(value || []), ...items]);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex">
          <input type="file" multiple hidden onChange={onFiles} data-testid={`${testId}-input`} />
          <Button type="button" variant="outline" size="sm" disabled={busy} asChild className="gap-1.5 cursor-pointer">
            <span>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Bijlage toevoegen</span>
          </Button>
        </label>
        {total > 0 && <span className="text-[11px] text-slate-400">{(total / 1024 / 1024).toFixed(1)} / {maxTotalMB} MB</span>}
      </div>
      {(value || []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((a, i) => (
            <span key={i} data-testid={`${testId}-chip-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 pl-2 pr-1 py-1 text-xs text-slate-700 max-w-[220px]">
              {(a.content_type || "").startsWith("image/") ? <ImageIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
              <span className="truncate">{a.filename}</span>
              <button type="button" onClick={() => setValue(value.filter((_, j) => j !== i))}
                className="text-slate-400 hover:text-red-500" data-testid={`${testId}-remove-${i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
